import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { CheckboxField } from '@/components/form-fields';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { adminApi } from '@/lib/admin-api';
import { STAFF_RESOURCES } from '@/lib/staff-resources';

/**
 * Invite a member.
 *
 * A dialog despite the usual "no modal for a small form" rule, and for the
 * reason ctrl-saas gives: this is a ten-field employee record — identity, role,
 * compliance numbers and the capability matrix — opened from a list, not a
 * two-field prompt. Two columns so it reads as a form rather than a stack.
 *
 * Nested inside the settings dialog. Radix stacks them correctly and dismissing
 * this one leaves settings open, which is what you want after sending an invite:
 * the list underneath is where you check it landed.
 */

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

const formSchema = z.object({
  email: z.string().trim().min(1, 'Enter an email address').email('Enter a valid email address'),
  role: z.string().min(1, 'Choose a role'),
  firstName: z.string().trim().max(80),
  lastName: z.string().trim().max(80),
  badgeNumber: z.string().trim().max(80),
  driversLicenseNumber: z.string().trim().max(80),
  driversLicenseExpiresAt: z.string(),
  iconColor: z.string().regex(HEX_COLOR, 'Pick a colour'),
  resources: z.array(z.string()),
});

type InviteForm = z.infer<typeof formSchema>;

function emptyForm(defaultRole: string): InviteForm {
  return {
    email: '',
    role: defaultRole,
    firstName: '',
    lastName: '',
    badgeNumber: '',
    driversLicenseNumber: '',
    driversLicenseExpiresAt: '',
    iconColor: '#16a34a',
    resources: [],
  };
}

export function InviteMemberDialog({
  accountId,
  roles,
}: {
  accountId: string;
  /** Only what the caller may hand out — the API filters by their own rank. */
  roles: string[];
}) {
  /**
   * `roles` arrives ranked most-elevated first, so `roles[0]` is the caller's
   * own rank — for an owner that would open the form pre-set to invite another
   * owner. Prefer the baseline `member`, then fall back to the least elevated
   * role on offer: a workspace that added a custom role below `member` should
   * not silently make it the default either.
   */
  const defaultRole = roles.find((role) => role === 'member') ?? roles.at(-1) ?? 'member';

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<InviteForm>(() => emptyForm(defaultRole));
  const [errors, setErrors] = useState<Partial<Record<keyof InviteForm, string>>>({});

  const queryClient = useQueryClient();

  const invite = useMutation({
    mutationFn: (values: InviteForm) =>
      adminApi.team.invite(accountId, {
        ...values,
        // The API treats '' as "not provided"; sending it either way keeps this
        // component from having to know which fields are nullable.
        resources: values.resources,
      }),
    onSuccess: (result, values) => {
      /*
       * The invitation row exists whether or not the email went out — the API
       * deliberately does not fail the request on a mail error, because the
       * operator's next move (Resend) is in the list they are about to see.
       * Saying which happened is the difference between "done" and "check the
       * mail settings".
       */
      if (result.emailSent) {
        toast.success(`Invitation sent to ${values.email}`);
      } else {
        toast.warning('Invitation created, but the email could not be sent', {
          description: 'Use Resend on the invitation once email delivery is configured.',
        });
      }

      queryClient.invalidateQueries({ queryKey: ['team', accountId] });
      close(false);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function close(next: boolean) {
    setOpen(next);

    if (!next) {
      setForm(emptyForm(defaultRole));
      setErrors({});
      invite.reset();
    }
  }

  function set<K extends keyof InviteForm>(key: K, value: InviteForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const parsed = formSchema.safeParse(form);

    if (!parsed.success) {
      const next: Partial<Record<keyof InviteForm, string>> = {};

      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof InviteForm;
        next[field] ??= issue.message;
      }

      setErrors(next);
      return;
    }

    invite.mutate(parsed.data);
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger asChild>
        <Button size="sm">
          <UserPlus className="size-4" />
          Invite member
        </Button>
      </DialogTrigger>

      {/* Column flex with a scrolling body, for the same reason the settings
          dialog is: a grid would size the body to its content and overflow. */}
      <DialogContent className="flex max-h-[85svh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="shrink-0 border-b px-6 py-5">
          <DialogTitle>Invite a team member</DialogTitle>
          <DialogDescription>
            They get an email to join this workspace. Badge and licence numbers are kept on the
            store's record for compliance.
          </DialogDescription>
        </DialogHeader>

        {/* noValidate for the same reason as the store form: native constraint
            validation on `type="email"` pre-empts the submit event, so zod's
            messages would never render for a malformed address. */}
        <form onSubmit={handleSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
          {/*
            Native overflow rather than <ScrollArea>, which the settings dialog
            uses. Radix sizes its viewport with `height: 100%`, and a percentage
            needs a definite height to resolve against — this dialog is sized by
            `max-h`, so every height in the chain is content-derived and the
            viewport grew to fit the form instead of scrolling it, putting the
            footer on top of the last two fields. The settings dialog gets away
            with it because it sets an explicit `h-[…]`.
          */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-7 px-6 py-6">
              <FieldSection title="Who they are">
                <Field className="sm:col-span-2" data-invalid={!!errors.email}>
                  <FieldLabel htmlFor="invite-email">Email</FieldLabel>
                  <Input
                    id="invite-email"
                    type="email"
                    autoComplete="off"
                    placeholder="budtender@farmhouse.com"
                    value={form.email}
                    onChange={(event) => set('email', event.target.value)}
                    aria-invalid={!!errors.email}
                  />
                  <FieldDescription>
                    The invitation is sent here and can only be accepted from this address.
                  </FieldDescription>
                  {errors.email && <FieldError>{errors.email}</FieldError>}
                </Field>

                <Field>
                  <FieldLabel htmlFor="invite-first-name">First name</FieldLabel>
                  <Input
                    id="invite-first-name"
                    value={form.firstName}
                    onChange={(event) => set('firstName', event.target.value)}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="invite-last-name">Last name</FieldLabel>
                  <Input
                    id="invite-last-name"
                    value={form.lastName}
                    onChange={(event) => set('lastName', event.target.value)}
                  />
                </Field>
              </FieldSection>

              <FieldSection
                title="Role & identity"
                description="The role sets what they can administer. The colour identifies them on the queue and delivery boards."
              >
                <Field>
                  <FieldLabel htmlFor="invite-role">Role</FieldLabel>

                  <Select value={form.role} onValueChange={(value) => set('role', value)}>
                    <SelectTrigger id="invite-role" className="w-full capitalize">
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role} value={role} className="capitalize">
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <FieldDescription>
                    An owner can reach every setting, billing and the team itself. A member only
                    gets the capabilities ticked below.
                  </FieldDescription>
                </Field>

                <Field data-invalid={!!errors.iconColor}>
                  <FieldLabel htmlFor="invite-color">Colour</FieldLabel>

                  <div className="flex items-center gap-2">
                    {/* No colour picker in the kit; the native input is the
                        whole implementation. */}
                    <input
                      type="color"
                      value={form.iconColor}
                      onChange={(event) => set('iconColor', event.target.value)}
                      className="border-input h-9 w-12 shrink-0 cursor-pointer rounded-md border bg-transparent p-1"
                      aria-label="Pick a colour"
                    />

                    <Input
                      id="invite-color"
                      value={form.iconColor}
                      onChange={(event) => set('iconColor', event.target.value)}
                      className="font-mono"
                      spellCheck={false}
                      aria-invalid={!!errors.iconColor}
                    />
                  </div>

                  <FieldDescription>Shown on queue and delivery boards.</FieldDescription>
                  {errors.iconColor && <FieldError>{errors.iconColor}</FieldError>}
                </Field>
              </FieldSection>

              <FieldSection
                title="Compliance record"
                description="Optional now — you can fill these in once they accept."
              >
                <Field>
                  <FieldLabel htmlFor="invite-badge">Badge number</FieldLabel>
                  <Input
                    id="invite-badge"
                    value={form.badgeNumber}
                    onChange={(event) => set('badgeNumber', event.target.value)}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="invite-licence">Driver's licence</FieldLabel>
                  <Input
                    id="invite-licence"
                    value={form.driversLicenseNumber}
                    onChange={(event) => set('driversLicenseNumber', event.target.value)}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="invite-licence-expiry">Licence expiry</FieldLabel>
                  <Input
                    id="invite-licence-expiry"
                    type="date"
                    value={form.driversLicenseExpiresAt}
                    onChange={(event) => set('driversLicenseExpiresAt', event.target.value)}
                  />
                </Field>
              </FieldSection>

              <CapabilityPicker
                selected={form.resources}
                onToggle={(value, on) =>
                  set(
                    'resources',
                    on
                      ? [...form.resources, value]
                      : form.resources.filter((item) => item !== value),
                  )
                }
              />
            </div>
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t px-6 py-4">
            <Button type="button" variant="outline" onClick={() => close(false)}>
              Cancel
            </Button>

            <Button type="submit" disabled={invite.isPending}>
              {invite.isPending ? 'Sending…' : 'Send invite'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A titled two-column band inside the form. */
function FieldSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-y-0.5">
        <h3 className="text-sm font-medium">{title}</h3>
        {description && <p className="text-muted-foreground text-xs">{description}</p>}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

/**
 * The `resources[]` matrix.
 *
 * Owners and anyone with `members.manage` implicitly hold every capability, so
 * this only changes what a plain member can reach — which the footnote says
 * rather than the control silently doing nothing for an owner.
 */
function CapabilityPicker({
  selected,
  onToggle,
}: {
  selected: string[];
  onToggle: (value: string, on: boolean) => void;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-y-0.5">
        <h3 className="text-sm font-medium">Capabilities</h3>
        <p className="text-muted-foreground text-xs">
          What this person can do on the floor, and which sections appear in their sidebar. Owners
          and members-managers already hold all of these.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        {STAFF_RESOURCES.map((resource) => (
          <CheckboxField
            key={resource.value}
            id={`capability-${resource.value}`}
            label={resource.label}
            description={resource.description}
            checked={selected.includes(resource.value)}
            onChange={(checked) => onToggle(resource.value, checked)}
            className="hover:bg-accent rounded-md p-2 transition-colors"
          />
        ))}
      </div>
    </section>
  );
}
